import { _ } from 'i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { InputActionMeta, SingleValue } from 'react-select';
import Select from 'react-select';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export interface DebouncedAsyncSelectProps {
    onInputChange: (value: string) => void;
    onChange: (value: SingleValue<{ value: string | number, label: string }>) => void
    options?: { value: string | number; label: string }[];
    isLoading?: boolean;
    isClearable?: boolean
    debounceDelay?: number;
}

export function DebouncedAsyncSelect({
    onInputChange,
    options,
    isLoading = false,
    debounceDelay = 300,
    isClearable = true,
    onChange,
}: DebouncedAsyncSelectProps) {
    const [inputValue, setInputValue] = useState("");
    const inputSubjectRef = useRef(new Subject<string>());

    useEffect(() => {
        const subscription = inputSubjectRef.current
            .pipe(
                debounceTime(debounceDelay),
                distinctUntilChanged()
            )
            .subscribe((debouncedValue) => {
                onInputChange(debouncedValue);
            });

        return () => subscription.unsubscribe();
    }, [debounceDelay, onInputChange]);

    const handleInputChange = useCallback(
        (value: string) => {
            setInputValue(value);
            inputSubjectRef.current.next(value);
            return value;
        },
        []
    );

    return (
        <Select
            noOptionsMessage={({ inputValue }) => (inputValue ? _("datatables.zero_records") : _("label.search"))}
            className="react-select"
            classNamePrefix="react-select"
            isClearable={isClearable}
            onChange={onChange}
            isLoading={isLoading}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            options={options}
        />
    );
}